class SlumberCalloutBanner extends HTMLElement {
  constructor() {
    super();
    this.onResize = this.onResize.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDesignModeChange = this.onDesignModeChange.bind(this);
  }

  connectedCallback() {
    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.initialize();

    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:reorder', this.onSectionLoad);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.onDesignModeChange);
      document.addEventListener('shopify:block:deselect', this.onDesignModeChange);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:section:reorder', this.onSectionLoad);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('shopify:block:select', this.onDesignModeChange);
    document.removeEventListener('shopify:block:deselect', this.onDesignModeChange);
    this.resizeObserver?.disconnect();
    this.calloutObservers?.forEach((observer) => observer.disconnect());
    this.abortController?.abort();
  }

  initialize() {
    this.media = this.querySelector('.slumber-callout-banner__media');
    this.svg = this.querySelector('.slumber-callout-banner__lines');

    if (!this.media || !this.svg) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.calloutObservers?.forEach((observer) => observer.disconnect());
    this.calloutObservers = [];

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => this.drawLines());
    this.resizeObserver.observe(this.media);

    this.callouts = Array.from(this.querySelectorAll('.slumber-callout'));

    this.callouts.forEach((callout) => {
      const dot = callout.querySelector('.slumber-callout__dot');
      if (!dot) return;

      dot.addEventListener(
        'click',
        (event) => {
          if (!this.isMobile()) return;

          event.preventDefault();
          event.stopPropagation();
          this.toggleCallout(callout);
        },
        { signal }
      );

      const calloutObserver = new MutationObserver(() => {
        window.requestAnimationFrame(() => this.drawLines());
      });

      calloutObserver.observe(callout, {
        attributes: true,
        attributeFilter: ['style', 'data-target-x', 'data-target-y', 'data-box-x', 'data-box-y', 'data-connect-side', 'data-line-direction', 'data-align-target-y', 'class'],
      });

      this.calloutObservers.push(calloutObserver);
    });

    this.media.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => this.drawLines(), { once: true, signal });
      }
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.drawLines());
    } else {
      this.drawLines();
    }
  }

  isMobile() {
    return this.mobileQuery.matches;
  }

  toggleCallout(callout) {
    const isActive = callout.classList.contains('is-active');

    this.closeAll();

    if (!isActive) {
      callout.classList.add('is-active');
      callout.querySelector('.slumber-callout__dot')?.setAttribute('aria-expanded', 'true');
      window.requestAnimationFrame(() => {
        this.clampCalloutCard(callout);
        this.drawLines();
      });
    }
  }

  closeAll() {
    let didClose = false;

    this.callouts.forEach((callout) => {
      if (callout.classList.contains('is-active')) {
        didClose = true;
      }

      callout.classList.remove('is-active');
      callout.querySelector('.slumber-callout__dot')?.setAttribute('aria-expanded', 'false');
      callout.querySelector('.slumber-callout__card')?.style.removeProperty('--callout-card-shift-x');
    });

    if (didClose) {
      this.drawLines();
    }
  }

  clampCalloutCard(callout) {
    const card = callout.querySelector('.slumber-callout__card');
    if (!card || !this.isMobile()) return;

    card.style.removeProperty('--callout-card-shift-x');

    const padding = 12;
    const rect = card.getBoundingClientRect();
    let shift = 0;

    if (rect.right > window.innerWidth - padding) {
      shift = window.innerWidth - padding - rect.right;
    } else if (rect.left < padding) {
      shift = padding - rect.left;
    }

    if (shift !== 0) {
      card.style.setProperty('--callout-card-shift-x', `${shift}px`);
    }
  }

  onDocumentClick(event) {
    if (!this.isMobile()) return;
    if (this.contains(event.target)) return;
    this.closeAll();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeAll();
  }

  onSectionLoad(event) {
    const banner = event.target.querySelector('slumber-callout-banner');

    if (banner === this) {
      this.initialize();
      return;
    }

    if (event.target.contains(this)) {
      this.initialize();
    }
  }

  onDesignModeChange(event) {
    if (!this.contains(event.target)) return;
    window.requestAnimationFrame(() => this.drawLines());
  }

  onResize() {
    window.requestAnimationFrame(() => {
      if (!this.isMobile()) {
        this.closeAll();
      } else {
        this.callouts
          .filter((callout) => callout.classList.contains('is-active'))
          .forEach((callout) => this.clampCalloutCard(callout));
      }

      this.drawLines();
    });
  }

  drawLines() {
    if (!this.media || !this.svg) return;

    const mediaRect = this.media.getBoundingClientRect();

    if (!mediaRect.width || !mediaRect.height) return;

    const lines = [];
    const isMobile = this.isMobile();
    const callouts = Array.from(this.querySelectorAll('.slumber-callout'));

    callouts.forEach((callout) => {
      const dot = callout.querySelector('.slumber-callout__dot');
      const card = callout.querySelector('.slumber-callout__card');

      if (!dot || !card) return;

      const connectSide = callout.dataset.connectSide || 'right';
      const lineDirection = callout.dataset.lineDirection || 'auto';
      const target = this.getTargetPoint(callout, mediaRect);
      const isActive = callout.classList.contains('is-active');
      const calloutId = callout.id || '';

      if (Number.isNaN(target.x) || Number.isNaN(target.y)) return;

      if (isMobile && !isActive) {
        dot.style.left = `${target.x}px`;
        dot.style.top = `${target.y}px`;
        return;
      }

      const cardRect = card.getBoundingClientRect();
      const attach = this.getAttachPoint(cardRect, mediaRect, connectSide);
      const points = this.getLinePoints(target, attach, connectSide, lineDirection);

      dot.style.left = `${attach.x}px`;
      dot.style.top = `${attach.y}px`;

      lines.push(this.createPolyline(points, calloutId, isActive || !isMobile));
    });

    this.svg.setAttribute('viewBox', `0 0 ${mediaRect.width} ${mediaRect.height}`);
    this.svg.innerHTML = lines.join('');
  }

  getTargetPoint(callout, mediaRect) {
    const targetX = (parseFloat(callout.dataset.targetX) / 100) * mediaRect.width;
    const targetY = (parseFloat(callout.dataset.targetY) / 100) * mediaRect.height;

    return { x: targetX, y: targetY };
  }

  getLinePoints(target, attach, connectSide, lineDirection) {
    const horizontalLast = this.useHorizontalLastSegment(connectSide, lineDirection);

    if (horizontalLast) {
      if (Math.abs(target.y - attach.y) < 1) {
        return [
          [target.x, target.y],
          [attach.x, attach.y],
        ];
      }

      return [
        [target.x, target.y],
        [target.x, attach.y],
        [attach.x, attach.y],
      ];
    }

    if (Math.abs(target.x - attach.x) < 1) {
      return [
        [target.x, target.y],
        [attach.x, attach.y],
      ];
    }

    return [
      [target.x, target.y],
      [attach.x, target.y],
      [attach.x, attach.y],
    ];
  }

  useHorizontalLastSegment(connectSide, lineDirection) {
    if (lineDirection === 'vertical_first') return true;
    if (lineDirection === 'horizontal_first') return false;

    return connectSide === 'left' || connectSide === 'right';
  }

  getAttachPoint(cardRect, mediaRect, side) {
    switch (side) {
      case 'left':
        return {
          x: cardRect.left - mediaRect.left,
          y: cardRect.top + cardRect.height / 2 - mediaRect.top,
        };
      case 'top':
        return {
          x: cardRect.left + cardRect.width / 2 - mediaRect.left,
          y: cardRect.top - mediaRect.top,
        };
      case 'bottom':
        return {
          x: cardRect.left + cardRect.width / 2 - mediaRect.left,
          y: cardRect.bottom - mediaRect.top,
        };
      case 'right':
      default:
        return {
          x: cardRect.right - mediaRect.left,
          y: cardRect.top + cardRect.height / 2 - mediaRect.top,
        };
    }
  }

  createPolyline(points, calloutId, isVisible) {
    const pathPoints = points
      .map((point) => {
        const [x, y] = point;
        return `${x},${y}`;
      })
      .join(' ');

    const activeClass = isVisible ? ' is-active' : '';

    return `<polyline class="slumber-callout-line${activeClass}" data-callout-id="${calloutId}" points="${pathPoints}" />`;
  }
}

customElements.define('slumber-callout-banner', SlumberCalloutBanner);
